import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import rewardsRoutes from '../../routes/rewards';
import orderingRoutes from '../../routes/ordering';
import { dispatchLogoutAction } from '../../actions/authentication';
import {
  fetchAppStartupInfoAction,
  clearApiErrorResponseAction,
  dispatchContextualHome,
  dispatchStartupInfoAndResourceDataAction,
} from '../../actions/common';
import {
  fetchRewardGuestDetailsAction,
  dispatchCallbackUrlAction,
  dispatchRewardGuestProfileAction,
  fetchReferralCodeAction,
  generateReferralCodeAction,
} from '../../actions/rewards';
import { checkExisitingRewardGuestApi } from '../../api/api';
import {
  matchRoutePatternWithWildcardSupport,
  extractWhitelabelSubdomain,
  isIosWebView,
  getDeviceType,
  compareVersions,
  getComponentVersionProps,
  isIosWebViewWrapper,
} from '../../utils/helpers';
import { isNativeWebView } from '../../utils/helpers';
import { getItem, setItem } from '../../utils/storage';
import {
  restaurantDataDepthParams,
  storageTypes,
  apiResponseCodes,
  transitionType,
  offerTypesConfig,
} from '../../constants/globals';
import { getErrorTitle, getErrorDescription } from '../../selectors';
import { navigationalPanelRoutes, nativeEventType, brandModuleCodes } from './config';
import {
  clearViewCartItems,
  updateCartItemsAction,
  updateRestaurantMenuAction,
} from '../../../src/actions/ordering';
import {
  checkBrandModuleApplicable,
  extractDataFromNative,
  getOrderingRouteContext,
  handleLinkUrl,
} from './helpers';
import { useDidUpdateEffect, useTheme } from '../../utils/customHooks';
import { RewardsGreeting } from '../../components/SplashScreen/RewardsGreeting';
import resourcesConfig from '../../resources';
import DefaultPage from '../Default';
import ThemeContext from '../ThemeContext';
import DefaultRegularFont from '../../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../../assets/fonts/OpenSans-Bold.ttf';
import { addItemInCart, removeItemFromCart } from '../../containers/Ordering/helpers';
import './index.scss';

const deviceType = getDeviceType();

// rewards container import
const Authentication = asyncRoute(() => import('./authentication'));
const Home = asyncRoute(() => import('./Home'));
const Ordering = asyncRoute(() => import('../Ordering'));
const Account = asyncRoute(() => import('./Account'));
const Locations = asyncRoute(() => import('./Locations'));
const GetApp = asyncRoute(() => import('./GetApp'));
const RewardList = asyncRoute(() => import('./RewardList'));
const CouponsList = asyncRoute(() => import('./CouponsList'));
const RewardDetails = asyncRoute(() => import('./RewardDetails'));
const RewardProgramDetails = asyncRoute(() => import('./RewardProgramDetails'));
const Referral = asyncRoute(() => import('./Referral'));
const AboutUs = asyncRoute(() => import('./AboutUs'));
const FranchiseEnquiry = asyncRoute(() => import('./FranchiseEnquiry'));
const DeleteAccountSuccess = asyncRoute(() => import('./DeleteAccountSuccess'));
const LocationDetails = asyncRoute(() => import('./LocationDetails'));
const HelpAndSupport = asyncRoute(() => import('./HelpAndSupport'));
const Scan = asyncRoute(() => import('./Scan'));
const VIPPassList = asyncRoute(() => import('./VIPPassList'));
const VIPPassDetails = asyncRoute(() => import('./VIPPassDetails'));
const VIPMembershipList = asyncRoute(() => import('./VIPMembershipList'));
const Payment = asyncRoute(() => import('./Payment'));
const MyRewards = asyncRoute(() => import('./MyRewards'));
const SelectOutlet = asyncRoute(() => import('./SelectOutlet'));
const Location = asyncRoute(() => import('./Location'));
const PayAndEarn = asyncRoute(() => import('./PayAndEarn'));
const Delivery = asyncRoute(() => import('../Ordering/Delivery'));
const BookTable = asyncRoute(() => import('./BookTable'));
const TableBookings = asyncRoute(() => import('./TableBookings'));
const FeedbackSuccessPage = asyncRoute(() => import('../Ordering/Feedback'));

// rewards components import

const NavigationPanel = asyncRoute(() => import('./components/NavigationPanel'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const NonExistentSubdomainFallback = asyncRoute(() =>
  import('../Fallback/NonExistentSubdomainFallback'),
);
const AppUpdatePopover = asyncRoute(() => import('./components/AppUpdatePopover'));

const isRewardsGreeterRendered = getItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered');

const Rewards = ({
  match,
  location,
  errorResponse,
  actions,
  history,
  resources,
  themeData,
  contextualHome,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
  rewardGuestDetails,
  appStartupInfo,
  selectedTable,
  cartItems,
  isWebApplicable,
  hasTiers,
  defaultWebRedirectionUrl,
  viewCartItems,
  restaurant,
  brandGuestId,
  instantDealRedemption,
  selectedAddress,
  cartOfferedItems,
  applicableBrandModules,
  referralProgram,
  appDownloadOneLinkUrl,
  ...props
}) => {
  const [activeTabContext, setActiveTabContext] = useState(rewardsRoutes.containers.home);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorContent, setErrorContent] = useState({});
  const [brandResources, setBrandResources] = useState(resources);
  const [showAppUpdatePopover, setShowAppUpdatePopover] = useState(false);
  const [showWebFooter, setShowWebFooter] = useState(!isNativeWebView);
  const [showShareAppFeature, setShowShareAppFeature] = useState(true);
  const [startupInteractionDone, setStartupInteractionDone] = useState(false);
  const [nativeRouteName, setNativeRouteName] = useState('');

  const [greetingSplashActive, setGreetingSplashActive] = useState(
    isRewardsGreeterRendered ? false : true,
  );
  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);
  const pathname = get(location, 'pathname', '');
  const showGreeterScreen = get(brandResources, 'showGreeterScreen', true);
  const isThemeApplied = useTheme('rewards-container', themeData);
  const callbackUrlRef = useRef();
  const cartItemsRef = useRef(cartItems);
  const viewCartRef = useRef();

  const selectedTableRef = useRef(selectedTable);

  const isReferralApplicable = checkBrandModuleApplicable({
    applicableBrandModules,
    moduleName: brandModuleCodes.referral,
  });

  const authenticationExemptedRoutes = isNativeWebView
    ? [rewardsRoutes.containers.getApp]
    : [rewardsRoutes.containers.getApp, rewardsRoutes.containers.scan];

  useEffect(() => {
    if (!isNativeWebView) {
      if (!isWebApplicable) {
        setGreetingSplashActive(false);
      }
    }
  }, [isWebApplicable]);

  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  useEffect(() => {
    setItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered', true);
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const subDomain = extractWhitelabelSubdomain();
    const { pathname, search } = location;
    let callbackUrl = search ? `${pathname}${search}` : pathname;
    callbackUrlRef.current = callbackUrl;
    let activePathNames = pathname && pathname.split('/');
    let activePathName = activePathNames[2];
    if (subDomain) {
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['reward', 'brand', 'resource'],
      };
      if (isNativeWebView) {
        const startupInfoData = {
          eventType: { type: nativeEventType.appStartupInfo },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(startupInfoData));
        // fetch Startup Info
        fetchStartupInfoData({ appStartupReqBody });
        // event listner for ios
        let dataFromApp = '';
        window.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');
          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;

            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
            } else {
              // fetch Startup Info
              fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.brandGuestData) && !isEmpty(data)) {
              actions.dispatchRewardGuestProfile(data);
            }
          } else {
            // fetch Startup Info
            fetchStartupInfoData({ appStartupReqBody });
          }
        });

        // event listner for android
        document.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');

          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;
            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
              setStartupInteractionDone(true);
            } else {
              // fetch Startup Info
              fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.brandGuestData) && !isEmpty(data)) {
              actions.dispatchRewardGuestProfile(data);
            }
          } else {
            // fetch Startup Info
            fetchStartupInfoData({ appStartupReqBody });
          }
        });
      } else {
        // fetch Startup Info
        fetchStartupInfoData({ appStartupReqBody });
        if (
          callbackUrl.includes(
            `/${rewardsRoutes.rewards}/${orderingRoutes.ordering}/${orderingRoutes.containers.menu}/`,
          )
        ) {
          history.push(
            `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.scan}/${
              activePathNames[activePathNames.length - 1]
            }`,
          );
        }
      }

      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
        const nativeAppUpdateAvailable = {
          eventType: { type: nativeEventType.nativeAppUpdateAvailable },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(nativeAppUpdateAvailable));
        const appVersionData = {
          eventType: { type: nativeEventType.appVersion },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(appVersionData));

        // add event listeners for catch the event from web view
        // event listner for ios
        window.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            selectedTable: selectedTableRef.current,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
            handleClearViewCartItems,
            handleUpdateCart,
            handleRestaurantSelection,
          });
        });
        // event listner for android
        document.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            selectedTable: selectedTableRef.current,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
            handleClearViewCartItems,
            handleUpdateCart,
            handleRestaurantSelection,
          });
        });
      }
    } else {
      history.push(callbackUrl);
    }

    // ignore greeter for get app
    const greeterExceptionalRoutes = [
      rewardsRoutes.containers.getApp,
      rewardsRoutes.containers.scan,
      rewardsRoutes.containers.delivery,
    ];

    if (greeterExceptionalRoutes.includes(activePathName)) {
      setGreetingSplashActive(false);
    }

    // component un mount logic
    return () => {
      actions.dispatchContextualHome('');
    };
  }, []);

  useEffect(() => {
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const subDomain = extractWhitelabelSubdomain();
    const { pathname, search } = location;
    let callbackUrl = callbackUrlRef.current
      ? callbackUrlRef.current
      : search
      ? `${pathname}${search}`
      : pathname;
    let activePathName = callbackUrl && callbackUrl.split('?')[0].split('/');
    activePathName = activePathName[2];
    if (startupInteractionDone) {
      if (!authenticationExemptedRoutes.includes(activePathName)) {
        const { config, phoneNumbers, workhours } = restaurantDataDepthParams;
        const url = `/brands/${subDomain}/outletDetails?size=100&depth=${[
          config,
          phoneNumbers,
          workhours,
        ]}`;
        //check for brand guest user
        if (isEmpty(rewardGuestDetails)) {
          if (!isEmpty(guestId) && !isEmpty(subDomain)) {
            const params = `isRewardOnboarded=true&brandId=${subDomain}&guestId=${guestId}`;
            checkExisitingRewardGuestApi(params).then((response) => {
              if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                const brandGuestId = get(response, 'data.id', '');
                if (get(response, 'data.innerError.isError')) {
                  history.push(
                    `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.authentication}/${
                      rewardsRoutes.authentication.signUp
                    }${search ? search : ''}`,
                  );
                } else {
                  if (isEqual(get(response, 'data.status'), 'DELETE_PENDING')) {
                    history.push(
                      `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.deleteAccountSuccess}`,
                    );
                    return;
                  }
                  if (!isEmpty(brandGuestId)) {
                    actions
                      .fetchRewardGuestDetails({ guestId: brandGuestId, depth: hasTiers })
                      .then((response) => {
                        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                          if (isReferralApplicable && !isEmpty(referralProgram)) {
                            handleFetchReferralCode({
                              brandGuestId: get(response, 'data.id', ''),
                              brandId: get(appStartupInfo, 'brandId'),
                            });
                          }
                          if (isNativeWebView) {
                            // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
                            const data = {
                              eventType: {
                                type: 'brandGuestData',
                                data: get(response, 'data', {}),
                              },
                            };
                            window.ReactNativeWebView.postMessage(JSON.stringify(data));
                          }
                          if (callbackUrl) {
                            history.push(callbackUrl);
                          } else {
                            history.push(`/${rewardsRoutes.rewards}`);
                          }
                        }
                      });
                  }
                }
              }
            });
          } else {
            actions.dispatchCallbackUrl({ callbackUrl }).then(() => {
              history.push(
                `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.authentication}/${
                  rewardsRoutes.authentication.signUp
                }${search ? search : ''}`,
              );
            });
          }
        } else {
          actions
            .fetchRewardGuestDetails({
              guestId: get(rewardGuestDetails, 'id', ''),
              depth: hasTiers,
            })
            .then((response) => {
              if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                if (isReferralApplicable && !isEmpty(referralProgram)) {
                  handleFetchReferralCode({
                    brandGuestId: get(response, 'data.id', ''),
                    brandId: get(appStartupInfo, 'brandId'),
                  });
                }
                if (isNativeWebView) {
                  // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
                  const data = {
                    eventType: { type: 'brandGuestData', data: get(response, 'data', {}) },
                  };
                  window.ReactNativeWebView.postMessage(JSON.stringify(data));
                }
                if (callbackUrl) {
                  history.push(callbackUrl);
                } else {
                  history.push(`/${rewardsRoutes.rewards}`);
                }
              }
            });
        }
      }
    }
  }, [startupInteractionDone]);

  useEffect(() => {
    selectedTableRef.current = selectedTable;
  }, [selectedTable]);

  useEffect(() => {
    if (isNativeWebView) {
      let activePathName = pathname && pathname.split('/');
      activePathName = activePathName[2];
      if (!isEqual(activePathName, rewardsRoutes.containers.authentication)) {
        const nativeAppUpdateAvailable = getItem(
          storageTypes.sessionStorage,
          'nativeAppUpdateAvailable',
        );
        if (!nativeAppUpdateAvailable) {
          const appVersion = getItem(storageTypes.sessionStorage, 'appVersion');
          const appUpdateConfigObj =
            brandResources &&
            brandResources.appUpdate &&
            brandResources.appUpdate.find((appConfig) => appConfig.platform === deviceType);
          const minReqAppVersion = get(appUpdateConfigObj, 'minRequiredAppVersion');
          const defaultAppVersion = get(appUpdateConfigObj, 'currentAppVersion');
          const webFooterApplicableVersion = get(appUpdateConfigObj, 'webFooterApplicableVersion');
          const currentAppVersion = appVersion ? appVersion : defaultAppVersion;

          if (!isEmpty(currentAppVersion)) {
            if (
              webFooterApplicableVersion &&
              compareVersions(currentAppVersion, webFooterApplicableVersion) < 0
            ) {
              setShowWebFooter(true);
              // Show footer of web until the user didn't install latest version
              if (!isIosWebView) {
                setShowShareAppFeature(false);
              }
            }
            if (minReqAppVersion && compareVersions(currentAppVersion, minReqAppVersion) < 0) {
              setShowAppUpdatePopover(true);
            } else {
              setShowAppUpdatePopover(false);
            }
          }
        }
      }
    }
  }, [pathname]);

  let pagesExistInNative = getItem(storageTypes.localStorage, 'pagesInNative') || [];

  useEffect(() => {
    if (
      (!greetingSplashActive && showGreeterScreen) ||
      !showGreeterScreen ||
      !isEmpty(pagesExistInNative)
    ) {
      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there

        const data = {
          eventType: { type: 'greeterScreenRendered' },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(data));
      }
    }
  }, [greetingSplashActive, showGreeterScreen]);

  useDidUpdateEffect(() => {
    setBrandResources(resources);
  }, [resources]);

  useEffect(() => {
    if (isNativeWebView) {
      // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
      const data = {
        eventType: { type: 'routeName', data: location },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  }, [location]);

  useEffect(() => {
    let activePathName = pathname && pathname.split('/');
    activePathName = activePathName[2];
    let redirectionUrl = '';
    if (!isEmpty(defaultWebRedirectionUrl)) {
      redirectionUrl = defaultWebRedirectionUrl;
    } else {
      redirectionUrl = `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.getApp}`;
    }
    if (
      !isWebApplicable &&
      !isNativeWebView &&
      !isEqual(rewardsRoutes.containers.scan, activePathName) &&
      !isEqual(rewardsRoutes.containers.getApp, activePathName)
    ) {
      if (!isEmpty(appDownloadOneLinkUrl)) {
        window.location.href = appDownloadOneLinkUrl;
      } else {
        history.push(redirectionUrl);
      }
    }
  }, [location, isWebApplicable]);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  useDidUpdateEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); // eslint-disable-line
    if (isNativeWebView && !isIosWebView) {
      // handling for android devices scroll(as above event stops scrolls in middle due to native webview event handling)
      setTimeout(() => {
        if (window.pageYOffset > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [pathname]);

  useEffect(() => {
    if (
      !isEmpty(errorResponse) &&
      ![
        'OTP_INVALID',
        'DEAL_ALREADY_ASSIGNED',
        'USER_BLOCKED',
        'PASS_SUBSCRIBE_ERROR',
        'DELIVERY_MIN_VALUE_ERR',
      ].includes(get(errorResponse, 'code'))
    ) {
      const { code, message } = errorResponse;
      const draftErrorContent = {
        title: getErrorTitle({ source: brandResources, code }),
        description: getErrorDescription({ source: brandResources, code, message }),
      };
      setShowErrorPopup(true);
      setErrorContent(draftErrorContent);
    }
  }, [errorResponse]);

  useEffect(() => {
    const restaurantId = get(restaurant, 'id', '');
    const scanCode = get(selectedTable, 'scanCode');
    let viewCartData = {
      ...viewCartItems,
      scanCode: scanCode,
    };
    if (
      isNativeWebView &&
      !isEqual(nativeRouteName, orderingRoutes.containers.menu) &&
      JSON.stringify(viewCartData) !== JSON.stringify(viewCartRef.current)
    ) {
      viewCartRef.current = viewCartData;
      const data = {
        eventType: { type: nativeEventType.viewCartData, data: viewCartData },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  }, [viewCartItems]);

  const handleLogout = () => {
    setItem('GuestLoginData', JSON.stringify({}));
    setItem('AuthData', JSON.stringify({}));
    actions.dispatchLogout();
    history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.authentication}`);
  };

  const handleNativeRouteName = (routeName) => {
    setNativeRouteName(routeName);
  };

  const handleClearViewCartItems = () => {
    actions.clearViewCartItems();
  };

  const handleRestaurantSelection = (data) => {
    actions.updateRestaurantMenu(data);
    handleClearViewCartItems();
  };

  const handleUpdateCart = ({ action, item, items }) => {
    let updatedCartItems = [];
    if (isEqual(action, 'view-cart')) {
      saveCart({ items: items });
    } else {
      const { isCustomizable, hasVariations } = item;
      if (isEqual(action, 'ADD')) {
        if (!(isCustomizable || hasVariations)) {
          updatedCartItems = addItemInCart({
            item,
            cartItems: cartItemsRef.current,
          });
        }
      } else {
        updatedCartItems = removeItemFromCart({
          item,
          cartItems: cartItemsRef.current,
        });
      }
      saveCart({ items: updatedCartItems });
    }
  };

  const saveCart = async ({
    items,
    onlyUpdate = false,
    withCustomization = true,
    cartOfferedItems,
    appliedOffer,
  }) => {
    let deliveryLocation;
    if (isEmpty(selectedAddress)) {
      deliveryLocation = {};
    } else {
      let selectedAddressToCart = selectedAddress;
      try {
        delete selectedAddressToCart.isServicable;
        delete selectedAddressToCart.id;
      } catch {
        console.log("can't delete");
      }
      deliveryLocation = { ...deliveryLocation, guestAddress: selectedAddressToCart };
    }
    const restaurantId = get(restaurant, 'id', '');
    const restaurantTableId = get(selectedTable, 'id', '');

    const type = get(selectedTable, 'type');
    const customization = get(viewCartItems, 'customization', '');
    let offeredItems = get(viewCartItems, 'offeredItems', []).filter(
      (item) => !isEmpty(item.offerId),
    );
    const orderOffersArr = get(viewCartItems, 'orderOffers', []);
    const appliedOrderOffer = orderOffersArr && orderOffersArr.find((offer) => offer);

    const appliedOffers =
      !isEmpty(get(viewCartItems, 'appliedOffers', [])) && !isEmpty(offeredItems)
        ? get(viewCartItems, 'appliedOffers', [])
        : [];
    const offerToBeApplied = appliedOffer ? appliedOffer : get(viewCartItems, 'appliedOffer', {});

    let cartItemsToAdd = {
      restaurantId,
      restaurantTableId,
      brandGuestId,
      items,
      deliveryLocation,
      isShowOffers: false,
      type,
      offeredItems,
    };
    if (instantDealRedemption) {
      cartItemsToAdd = { ...cartItemsToAdd, appliedOffer: offerToBeApplied };
    }
    if (!isEmpty(items)) {
      if (!instantDealRedemption) {
        if (
          !isEmpty(orderOffersArr) &&
          isEmpty(appliedOffers) &&
          (!isEmpty(offeredItems) ||
            !isEqual(get(appliedOrderOffer, 'type'), offerTypesConfig.bogo))
        ) {
          cartItemsToAdd['orderOffers'] = orderOffersArr;
        }
        if (!isEmpty(appliedOffers)) {
          cartItemsToAdd['appliedOffers'] = appliedOffers;
        }
      }
      if (withCustomization && !isEmpty(customization)) {
        cartItemsToAdd['customization'] = customization;
      }
    }
    await actions.updateCartItems(cartItemsToAdd);
  };

  const fetchStartupInfoData = ({ appStartupReqBody }) =>
    actions.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        actions.dispatchContextualHome(`/${rewardsRoutes.rewards}`);
        setStartupInteractionDone(true);
      } else {
        // incase if sub domain is not resolved
        setShowNonExistentSubdomainFallback(true);
      }
    });

  const handleFetchReferralCode = async ({ brandGuestId, brandId }) => {
    await actions.fetchReferralCode({ brandGuestId }).then((response) => {
      if (apiResponseCodes.NOT_FOUND.includes(get(response, 'status'))) {
        if (!isEmpty(brandGuestId) && !isEmpty(brandId)) {
          handleGenerateReferralCode({ brandGuestId, brandId });
        }
      }
    });
  };

  const handleGenerateReferralCode = async ({ brandGuestId, brandId }) => {
    await actions.generateReferralCode({
      generateReferralCodeReqBody: {
        brandGuestId,
        brandId,
      },
    });
  };

  const getActiveTabContext = (pathname) => {
    let currentActiveTabContext = activeTabContext;
    const paths = pathname.trim().split('/') || [];
    let engageRouteIndex = paths.indexOf(rewardsRoutes.rewards);
    if (engageRouteIndex !== -1) {
      currentActiveTabContext = paths[engageRouteIndex + 1];
    }
    return currentActiveTabContext;
  };

  const onCloseExceptionComponent = () => {
    actions.clearApiErrorResponse().then(() => {
      setShowErrorPopup(false);
      setErrorContent({});
    });
  };

  const getActiveRoute = (pathname) => {
    const paths = pathname.trim().split('/') || [];
    let route = '';
    let rewardRouteIndex = paths.indexOf(rewardsRoutes.rewards);
    if (rewardRouteIndex !== -1) {
      route = paths.slice(rewardRouteIndex + 1, paths.length).join('/');
    }
    return route;
  };

  const activeRoute = getActiveRoute(location.pathname);

  const handleNavTabClick = (type) => {
    switch (type) {
      case rewardsRoutes.containers.home:
        return history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.home}`);
      case rewardsRoutes.containers.locations:
        return history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.locations}`);
      case rewardsRoutes.containers.account:
        return history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.account}`);
      case rewardsRoutes.containers.ordering:
        let updatedRoute = getOrderingRouteContext(selectedTable, cartItems);
        return history.push(updatedRoute);
      default:
        return;
    }
  };

  const handleNonExistentSubdomainClick = () => {
    history.push(`/${rewardsRoutes.rewards}`);
    window.location.reload();
  };

  // Handle native back button
  const handleNativeBackCTA = (key) => {
    const backButtonElement = document.getElementById(key);
    if (backButtonElement !== null) {
      backButtonElement.click();
    } else {
      const entryRoutes = [rewardsRoutes.authentication, rewardsRoutes.containers.home];
      const pathname = get(location, 'pathname');
      if (!entryRoutes.some((entryRoute) => pathname.includes(entryRoute))) {
        history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.home}`);
      }
    }
  };

  const handleAppForceUpdateCTAClick = () => {
    const appUpdateConfigObj =
      brandResources &&
      brandResources.appUpdate &&
      brandResources.appUpdate.find((appConfig) => appConfig.platform === deviceType);
    if (!isEmpty(appUpdateConfigObj)) {
      const updateUrl = get(appUpdateConfigObj, 'updateUrl', '');
      handleLinkUrl({ linkUrl: updateUrl });
    }
  };

  const brandGuestName = get(rewardGuestDetails, 'name', '');

  const showGreeterText = get(brandResources, 'showGreeterText', true);
  const greeterVisibilityTimeInMS = get(brandResources, 'greeterVisibilityTimeInMS', 3000);
  const brandName = get(appStartupInfo, 'brand.name', '');
  const enableForceUpdate = get(brandResources, 'enableForceUpdate', false);
  const greeterStaticInfo = get(brandResources, 'staticPages', []).find((page) =>
    isEqual(page.route, 'greeter'),
  );
  const greeterHTML = get(greeterStaticInfo, 'content', '');

  const getStartedComponentVersion = get(
    getComponentVersionProps(themeData, 'auth-get-started'),
    'version',
    '',
  );
  const excludeTopSafeAreaComponents = ['cv2', 'cv3'];

  const excludeTopSafeArea =
    isNativeWebView &&
    excludeTopSafeAreaComponents.includes(getStartedComponentVersion) &&
    pathname.includes(rewardsRoutes.containers.authentication);

  const renderRoutes = () => (
    <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Switch location={location} key={location.pathname}>
          <Route
            exact
            path={`${match.path}`}
            render={() => <Redirect to={`${match.path}/${rewardsRoutes.containers.home}`} />}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.scan}/:scanCode`}
            component={Scan}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.authentication}`}
            component={Authentication}
          />

          <Route path={`${match.path}/${rewardsRoutes.containers.home}`} component={Home} />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.ordering}`}
            render={(props) => <Ordering {...props} />}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.account}`}
            render={(props) => <Account showShareAppFeature={showShareAppFeature} {...props} />}
          />
          <Route path={`${match.path}/${rewardsRoutes.containers.getApp}`} component={GetApp} />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.rewardList}`}
            component={RewardList}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.coupons}`}
            component={CouponsList}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.dealDetail}/:dealCode`}
            component={RewardDetails}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.deals}/:dealCode`}
            component={RewardDetails}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.couponDetail}/:couponCode`}
            component={RewardDetails}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.rewardProgramDetails}`}
            component={RewardProgramDetails}
          />
          <Route path={`${match.path}/${rewardsRoutes.containers.referral}`} component={Referral} />
          <Route
            exact
            path={`${match.path}/${rewardsRoutes.containers.locations}`}
            component={Locations}
          />
          <Route
            exact
            path={`${match.path}/${rewardsRoutes.containers.location}`}
            component={Location}
          />
          <Route
            exact
            path={`${match.path}/${rewardsRoutes.containers.selectOutlet}/:brandId`}
            component={SelectOutlet}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.locations}/:outletId`}
            component={LocationDetails}
          />
          <Route path={`${match.path}/${rewardsRoutes.containers.aboutUs}`} component={AboutUs} />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.franchiseEnquiry}`}
            component={FranchiseEnquiry}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.deleteAccountSuccess}`}
            component={DeleteAccountSuccess}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.helpSupport}`}
            component={HelpAndSupport}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.vipPassList}`}
            component={VIPPassList}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.vipPassDetail}/:passCode`}
            component={VIPPassDetails}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.vipMembershipList}`}
            component={VIPMembershipList}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.myRewards}`}
            component={MyRewards}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.payEarn}`}
            component={PayAndEarn}
          />
          <Route
            exact
            path={`${match.path}/feedback-success/:feedbackId`}
            component={FeedbackSuccessPage}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.bookTable}`}
            component={BookTable}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.tableBookings}`}
            component={TableBookings}
          />
          <Route
            path={`${match.path}/${rewardsRoutes.containers.delivery}`}
            render={(props) => <Delivery key={props.location.search} {...props} />}
          />
          <Route
            exact
            path={`${match.path}/${rewardsRoutes.containers.payment}`}
            component={Payment}
          />
          {/* Default route */}
          {isEmpty(contextualHome) ? (
            <Route path="/" component={DefaultPage} />
          ) : (
            <Redirect to={contextualHome} />
          )}
        </Switch>
      )}
    </IntlProvider>
  );
  return (
    <div
      className={classNames('rewards', {
        'in-app-style': isIosWebViewWrapper(get(themeData, 'baseTheme', '')),
        excludeTopSafeArea: excludeTopSafeArea,
      })}
      id="rewards-container"
    >
      {/* render greeting splash screen here */}
      <ThemeContext.Provider
        value={{ themeContext: rewardsRoutes.rewards, nativeRouteName: nativeRouteName }}
      >
        {greetingSplashActive &&
          isThemeApplied &&
          showGreeterScreen &&
          !isEmpty(brandResources) &&
          isEmpty(pagesExistInNative) && (
            <RewardsGreeting
              active={greetingSplashActive}
              deactivate={() => setGreetingSplashActive(false)}
              splashBgImage={get(brandResources, 'greeterSplashScreen', '')}
              loggedInGuestName={brandGuestName}
              showTimeSalutation
              showGreeterText={showGreeterText}
              splashDuration={greeterVisibilityTimeInMS}
              brandSprite={get(brandResources, 'brandSprite', '')}
              showStaticGreeter={get(brandResources, 'showStaticGreeter', false)}
              greeterHTML={greeterHTML}
            />
          )}
        {enableForceUpdate && isNativeWebView && showAppUpdatePopover && (
          <AppUpdatePopover
            open={showAppUpdatePopover}
            brandName={brandName}
            onAppForceUpdateCTAClick={() => handleAppForceUpdateCTAClick()}
          />
        )}
        {isThemeApplied && (
          <>
            {!isEmpty(pagesExistInNative) ? (
              renderRoutes()
            ) : (
              <SwitchTransition component={null}>
                <CSSTransition key={location.pathname} timeout={300} classNames={transitionType}>
                  {renderRoutes()}
                </CSSTransition>
              </SwitchTransition>
            )}
            {showWebFooter &&
              matchRoutePatternWithWildcardSupport(navigationalPanelRoutes, activeRoute) && (
                <NavigationPanel
                  onNavTabClick={(type) => {
                    handleNavTabClick(type);
                  }}
                  activeTabContext={getActiveTabContext(get(location, 'pathname'))}
                />
              )}
            {showErrorPopup && (
              <ExceptionComponent
                pageType="HalfSize"
                openSegmentPopup={showErrorPopup}
                dismissSegmentPopup={onCloseExceptionComponent}
                title={get(errorContent, 'title')}
                description={get(errorContent, 'description')}
              />
            )}
          </>
        )}
      </ThemeContext.Provider>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  contextualHome: get(state, 'common.contextualHome', ''),
  errorResponse: get(state, 'common.errorResponse', {}),
  resources: get(state, 'common.resources', {}),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
  outletDetails: get(state, 'common.outletDetails', {}),
  rewardGuestDetails: get(state, 'rewards.rewardGuestDetails', {}),
  selectedTable: get(state, 'ordering.selectedTable', {}),
  cartItems: get(state, 'ordering.viewCart.items', []),
  cartOfferedItems: get(state, 'ordering.viewCart.offeredItems', []),
  viewCartItems: get(state, 'ordering.viewCart', {}),
  isWebApplicable: get(state, 'common.resources.isWebApplicable', true),
  hasTiers: get(state, 'common.appStartupInfo.rewardProgram.hasTiers', false),
  defaultWebRedirectionUrl: get(state, 'common.resources.defaultWebRedirectionUrl', ''),
  restaurant: get(state, 'ordering.restaurant', {}),
  brandGuestId: get(state, 'rewards.rewardGuestDetails.id', ''),
  instantDealRedemption: get(state, 'common.resources.instantDealRedemption', false),
  selectedAddress: get(state, 'ordering.selectedAddress', ''),
  referralProgram: get(state, 'common.appStartupInfo.rewardProgram.referralProgram.program', {}),
  applicableBrandModules: get(state, 'common.modules', []),
  appDownloadOneLinkUrl: get(state, 'common.resources.appDownloadOneLinkUrl', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
      fetchRewardGuestDetails: fetchRewardGuestDetailsAction,
      dispatchContextualHome,
      dispatchCallbackUrl: dispatchCallbackUrlAction,
      dispatchStartupInfoAndResourceData: dispatchStartupInfoAndResourceDataAction,
      dispatchLogout: dispatchLogoutAction,
      dispatchRewardGuestProfile: dispatchRewardGuestProfileAction,
      clearViewCartItems: clearViewCartItems,
      updateCartItems: updateCartItemsAction,
      updateRestaurantMenu: updateRestaurantMenuAction,
      fetchReferralCode: fetchReferralCodeAction,
      generateReferralCode: generateReferralCodeAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Rewards);
